// A Logitech wheel opened over WebHID: decodes its input reports and sends
// force feedback, range and LED commands.
//
// Output goes through a small scheduler. One-off commands (init, range) are
// queued in order; continuous channels (force, damper, friction, LEDs) only
// keep their latest value, so a slow USB write never builds up a backlog of
// stale forces. Only one sendReport is in flight at a time.

import * as g27 from './g27';
import { decode, layoutsFrom, readBits, type RawControls, type ReportLayout } from './hidReport';

/**
 * The G27 reports the shifter's reverse position outside its 22 declared
 * buttons, as bit 80 of the input report (after the pedals and the raw shifter
 * X/Y bytes; layout per sonik-br/lgff_wheel_adapter and SDL's lg4ff driver).
 * We expose it as one extra button after the declared ones.
 */
const G27_REVERSE_BIT = 80;
import type { ControlSource } from './source';

type Channel = 'force' | 'damper' | 'friction' | 'leds';
const CHANNELS: Channel[] = ['force', 'damper', 'friction', 'leds'];

export class HidWheel implements ControlSource {
  readonly kind = 'hid' as const;
  readonly controls: RawControls = { axes: [], buttons: [], hat: -1 };
  /** Axis whose velocity is tracked (the bound steering axis). */
  velocityAxis = 0;
  connected = false;
  /** Input reports received (for the setup screen's live status). */
  reportsIn = 0;
  reportsOut = 0;
  lastError: string | undefined;
  /** Called when a report changes any control (live setup screen, learn mode). */
  onInput: (() => void) | undefined;

  private layouts = new Map<number, ReportLayout>();
  private readonly queue: g27.Report[] = [];
  private readonly latest = new Map<Channel, g27.Report>();
  private readonly sent = new Map<Channel, string>();
  private sending = false;
  private lastWheel: number | undefined;
  private lastWheelAt = 0;
  private velocity = 0;

  constructor(readonly device: HIDDevice) {}

  get name(): string {
    return g27.wheelName(this.device.productId);
  }

  get key(): string {
    return `hid:${this.device.vendorId.toString(16)}:${this.device.productId.toString(16)}`;
  }

  /** True for wheels in native mode, which take range, force and LED commands. */
  get native(): boolean {
    return g27.NATIVE_WHEELS.has(this.device.productId);
  }

  get compatMode(): boolean {
    return this.device.productId === g27.PRODUCT.COMPAT;
  }

  /** A G29 with its mode switch on PS4, which this code can't drive. */
  get ps4Mode(): boolean {
    return this.device.productId === g27.PRODUCT.G29_PS4;
  }

  /** In compatibility mode: which wheel it is, if its USB name says. */
  get compatModel(): g27.NativeModel | undefined {
    return g27.modelFromName(this.device.productName);
  }

  /**
   * Steering axis velocity, normalised units per second (filtered). The wheel
   * only reports when something changes, so a stale estimate means it stopped.
   */
  get wheelVelocity(): number {
    return performance.now() - this.lastWheelAt > 25 ? 0 : this.velocity;
  }

  get axisNames(): string[] {
    return ['Wheel', 'Pedal Y', 'Pedal Z', 'Pedal Rz', 'Axis 5', 'Axis 6'];
  }

  async open(): Promise<void> {
    if (!this.device.opened) await this.device.open();
    this.layouts = layoutsFrom(this.device.collections);
    this.device.addEventListener('inputreport', this.onReport);
    this.connected = true;
    this.lastError = undefined;
  }

  /** Put the wheel into a known state for driving: no built-in spring, no forces, our range. */
  start(rangeDeg: number): void {
    if (!this.native) return;
    this.sent.clear();
    this.command(g27.stopAll(), g27.autocenterOff(), g27.setRange(rangeDeg));
  }

  setRange(rangeDeg: number): void {
    if (this.native) this.command(g27.setRange(rangeDeg));
  }

  /** Back to menu behaviour: forces off, a gentle built-in centring spring so the wheel doesn't flop. */
  idle(autocentre = 0.25): void {
    if (!this.native) return;
    this.latest.clear();
    this.sent.clear();
    this.command(g27.stopAll(), ...g27.autocenter(autocentre), g27.setLeds(0));
  }

  /** Constant force −1..1 in the wheel's own convention (sign calibration happens upstream). */
  setForce(level: number): void {
    this.channel('force', g27.constantForce(level));
  }

  setDamper(k: number): void {
    this.channel('damper', g27.damper(1, k));
  }

  setFriction(k: number): void {
    this.channel('friction', g27.friction(2, k));
  }

  setLeds(mask: number): void {
    this.channel('leds', g27.setLeds(mask));
  }

  /**
   * Last-ditch stop when the page is going away: write straight to the device,
   * bypassing the queue, since there may be no time for it to drain. The wheel
   * would otherwise keep playing its last force after the tab closes.
   */
  emergencyStop(): void {
    if (!this.connected || !this.native) return;
    for (const r of [g27.stopAll(), ...g27.autocenter(0.25), g27.setLeds(0)]) void this.device.sendReport(0, r).catch(() => undefined);
  }

  /** Ask a wheel in compatibility mode to re-enumerate in its native mode. */
  switchToNative(model: g27.NativeModel = this.compatModel ?? 'G27'): void {
    this.command(...g27.nativeMode(model));
  }

  async close(): Promise<void> {
    if (this.connected && this.native) {
      // Leave the wheel centring itself rather than limp or still pushing.
      this.idle(0.25);
      await this.flush();
    }
    this.device.removeEventListener('inputreport', this.onReport);
    this.connected = false;
    try {
      if (this.device.opened) await this.device.close();
    } catch {
      // Already gone (unplugged).
    }
  }

  /** Resolves once everything queued has been written (or failed). */
  async flush(timeoutMs = 300): Promise<void> {
    const until = performance.now() + timeoutMs;
    while ((this.sending || this.queue.length || this.dirty()) && performance.now() < until) {
      await new Promise((r) => setTimeout(r, 5));
    }
  }

  markDisconnected(): void {
    this.connected = false;
  }

  private command(...reports: g27.Report[]): void {
    this.queue.push(...reports);
    void this.pump();
  }

  private channel(ch: Channel, r: g27.Report): void {
    if (!this.native || !this.connected) return;
    this.latest.set(ch, r);
    void this.pump();
  }

  private dirty(): boolean {
    for (const ch of CHANNELS) {
      const r = this.latest.get(ch);
      if (r && this.sent.get(ch) !== g27.toHex(r)) return true;
    }
    return false;
  }

  private next(): { report: g27.Report; ch?: Channel } | undefined {
    const q = this.queue.shift();
    if (q) return { report: q };
    // Force first: it's the one that has to be fresh.
    for (const ch of CHANNELS) {
      const r = this.latest.get(ch);
      if (r && this.sent.get(ch) !== g27.toHex(r)) return { report: r, ch };
    }
    return undefined;
  }

  private async pump(): Promise<void> {
    if (this.sending) return;
    this.sending = true;
    try {
      for (let item = this.next(); item && this.connected; item = this.next()) {
        try {
          await this.device.sendReport(0, item.report);
          this.reportsOut++;
          if (item.ch) this.sent.set(item.ch, g27.toHex(item.report));
        } catch (e) {
          this.lastError = e instanceof Error ? e.message : String(e);
          // Don't spin on a wheel that refuses writes; mark the channel sent and move on.
          if (item.ch) this.sent.set(item.ch, g27.toHex(item.report));
        }
      }
    } finally {
      this.sending = false;
    }
  }

  private readonly onReport = (e: HIDInputReportEvent): void => {
    const layout = this.layouts.get(e.reportId);
    if (!layout) return;
    this.reportsIn++;
    decode(layout, e.data, this.controls);
    if (this.device.productId === g27.PRODUCT.G27 && layout.bits > G27_REVERSE_BIT) {
      this.controls.buttons[layout.buttons.length] = readBits(e.data, G27_REVERSE_BIT, 1, false) === 1;
    }
    const w = this.controls.axes[this.velocityAxis];
    const now = performance.now();
    if (w !== undefined) {
      if (this.lastWheel !== undefined && now > this.lastWheelAt) {
        const dt = Math.min(0.05, (now - this.lastWheelAt) / 1000);
        const v = (w - this.lastWheel) / dt;
        // Light low-pass: the 14-bit axis is clean, but report intervals jitter.
        const a = Math.min(1, dt / 0.008);
        this.velocity += a * (v - this.velocity);
      }
      this.lastWheel = w;
      this.lastWheelAt = now;
    }
    this.onInput?.();
  };
}
